import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import { useListAffiliations } from '~/hooks/apis/agent/useListAffiliations';
import useCurrentBrand from '~/hooks/useCurrentBrand';

const DEFAULT_SIZE_PER_PAGE = '20';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function AgentHubCommissionsAffiliationInput({ setCreatedConditions, existingConditions }: Props) {
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAffiliations, setSelectedAffiliations] = useState<CommissionOption>(existingConditions || []);
  const brand = useCurrentBrand();

  const { data, isFetching } = useListAffiliations(
    {
      page: '0',
      pageSize: DEFAULT_SIZE_PER_PAGE,
      search: debouncedSearch,
      brand: ensureAgentHubBrands(brand),
    },
    { enabled: debouncedSearch.length > 0 },
  );

  const result = useMemo(() => {
    if (!data?.rows) {
      return [];
    }

    return data.rows.map((affiliation) => ({
      value: affiliation.id,
      label: `${affiliation.name} - ${affiliation.region}`,
    }));
  }, [data]);

  const noOptionsText = useMemo(() => {
    if (!debouncedSearch) {
      return 'Start typing to search for affiliations';
    }
    if (data?.total === 0) {
      return 'No affiliations found';
    }
    return 'Start typing to search for affiliations';
  }, [debouncedSearch, data?.total]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 500),
    [],
  );

  const handleSaveCondition = () => {
    setCreatedConditions(
      {
        type: 'affiliation',
        value: selectedAffiliations,
      },
      true,
    );
  };

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (newInputValue) {
      debouncedSetSearch(newInputValue);
    }
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedAffiliations(value);
  };

  useEffect(() => {
    handleSaveCondition();
  }, [selectedAffiliations]);

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={isFetching}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              label="Affiliations"
              placeholder={selectedAffiliations.length > 0 ? '' : 'Affiliations'}
            />
          )}
          onInputChange={handleInputChange}
          value={selectedAffiliations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for affiliations
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsAffiliationInput;
